import axios from 'axios';
import {
  GetApiV1AssetsPresignedPostUrlData,
  AssetsService,
  type AssetUploadScope,
} from '@inference.engine/assets-api-client/web';
import { assetsHttp } from '@/lib/api/http-client';
import { logger } from '@/lib/utils/logger';
// `boundaries` types this directory as `lib`, which may not import `shared`. i18next's
// default export is the very singleton `shared/i18n/config` initialises, so reach for it
// directly rather than through the `@/shared/i18n` re-export.
import i18n from 'i18next';

type AllowedMimeType = GetApiV1AssetsPresignedPostUrlData['query']['contentType'];

/**
 * Result of {@link getFileUploadUrl} — pre-signed S3 upload coordinates.
 */
export interface FileUploadResult {
  url: string;
  assetId: string;
  fields: Record<string, string>;
}

/**
 * Request a pre-signed S3 upload URL for a new asset.
 *
 * Domain-agnostic infra utility: works for any feature that needs to upload
 * a file to the assets service. Promoted from `features/chats/api/chats.api`
 * (P11.2 /) since file upload is not chat-specific.
 *
 * Existing consumers: chats (internal — message attachments + document
 * conversion), admin (ExternalToolsPanel image upload), audiences
 * (persona profile media), templates-config (template image upload).
 */
export const getFileUploadUrl = async (
  mimeType: string,
  fileName: string,
  options?: { isPublic?: boolean; scope?: AssetUploadScope },
): Promise<FileUploadResult> => {
  try {
    const response = await AssetsService.getAssetUploadUrl({
      client: assetsHttp,
      query: {
        // Type assertion: validation happens server-side
        contentType: mimeType as AllowedMimeType,
        filename: fileName,
        ...(options?.isPublic != null && { isPublic: options.isPublic }),
        ...(options?.scope && { scope: options.scope }),
      },
    });

    if (!response.data) {
      const status = response.response?.status;
      const detail =
        typeof response.error === 'string' ? response.error : JSON.stringify(response.error);
      // eslint-disable-next-line i18next/no-literal-string -- log and Sentry text, never rendered
      const diagnostic = `No upload URL received (status: ${status ?? 'unknown'}, contentType: "${mimeType}", detail: ${detail})`;
      logger.error(diagnostic);
      // `logger.error` is stripped outside DEV, so the diagnostic also rides along as an
      // Error `cause` — Sentry's linked-errors integration only walks causes that are Errors.
      throw new Error(i18n.t('common:lib.fileUpload.noUploadUrl'), {
        cause: new Error(diagnostic),
      });
    }

    return {
      url: response.data.url,
      assetId: response.data.assetId,
      fields: response.data.fields as Record<string, string>,
    };
  } catch (error) {
    throw new Error(
      i18n.t('common:lib.fileUpload.uploadUrlFailed', {
        message:
          error instanceof Error
            ? error.message
            : i18n.t('common:components.sidePanel.unknownError'),
      }),
      { cause: error },
    );
  }
};

/**
 * Upload a file directly to S3 using a pre-signed POST URL produced by
 * {@link getFileUploadUrl}. Domain-agnostic.
 */
// Human-readable messages for S3 error <Code>s that users can act on.
// Consumers render `err.message` directly in an error toast, so the thrown
// message must stay short and human — the raw response is logged instead.
// Getters, not values: this table wins the `??` chain below over the two translated
// fallbacks, so anything resolved at module evaluation ships English in every locale.
const S3_ERROR_MESSAGES: Record<string, string> = {
  get EntityTooLarge() {
    return i18n.t('common:lib.fileUpload.fileTooLarge');
  },
  get AccessDenied() {
    return i18n.t('common:lib.fileUpload.uploadRejected');
  },
  get SignatureDoesNotMatch() {
    return i18n.t('common:lib.fileUpload.uploadLinkInvalid');
  },
  get ExpiredToken() {
    return i18n.t('common:lib.fileUpload.uploadLinkExpired');
  },
};

export const uploadFileToS3 = async (
  url: string,
  formData: FormData,
  abortSignal?: AbortSignal,
): Promise<void> => {
  try {
    // Upload file directly to S3 using the presigned URL
    await axios.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      signal: abortSignal,
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      // Log the full S3 response so a 403 is diagnosable by its actual
      // <Code> (e.g. EntityTooLarge, SignatureDoesNotMatch), but throw a short
      // human message — err.message ends up verbatim in the user-facing toast.
      const body =
        typeof error.response.data === 'string'
          ? error.response.data
          : JSON.stringify(error.response.data);
      logger.error(`File upload failed (status ${error.response.status}): ${body}`, error);
      const code = /<Code>([^<]+)<\/Code>/.exec(body)?.[1];
      const message =
        (code !== undefined ? S3_ERROR_MESSAGES[code] : undefined) ??
        (code
          ? i18n.t('common:lib.fileUpload.theFileUploadFailedWithCode', { code })
          : i18n.t('common:lib.fileUpload.theFileUploadFailed'));
      throw new Error(message, { cause: error });
    }
    logger.error('File upload failed:', error);
    throw error;
  }
};
